import { useEffect, useState } from "react";
import { DefaultButton, IconButton, TextField, Toggle } from "@fluentui/react";
import { t } from "i18next";

import ConditionBuilder from "./ConditionBuilder";
import LayoutDesigner from "./LayoutDesigner";
import { ReadField, readOption, readYesNo } from "./ReadOnly";
import SegmentedField from "./SegmentedField";
import ViewportInspector from "./ViewportInspector";
import { NO_FIELD_PROPS } from "../../helpers/hangingProtocol/hpFormFields";
import { PRIORS_UI_ENABLED } from "../../helpers/hangingProtocol/featureFlags";
import { createEmptyRuleGroup, newNodeId } from "../../helpers/hangingProtocol/hpDocument";
import {
  GROUP_KIND,
  LINK_SCROLL_SERIES,
  LINK_SCROLL_SERIES_OPTIONS,
  SYNC_ACTIONS,
  SYNC_ACTIONS_OPTIONS,
} from "../../helpers/hangingProtocol/protocolEnums";
import "./editors.css";

/**
 * One protocol — a MedDream "category".
 *
 * The layout is the centrepiece: the grid stays on screen and the selected
 * viewport's settings appear beside it, rather than MedDream's single scroll
 * where every viewport's controls are stacked inline.
 *
 * Categories under `hpDrag` / `hpComparison` omit every prior-related field, so
 * those sections are absent rather than disabled for those kinds. `hp` ones do
 * carry them, but the priors section is held behind `PRIORS_UI_ENABLED` until
 * the phase that ships prior authoring — the section itself stays exported and
 * working so that phase is a flag flip.
 *
 * Exported section by section as well as whole: the authoring wizard shows one
 * section per step, so the fields live here and the step order lives there.
 */

/**
 * `priorStudyConditions` and `otherStudyConditions` are *arrays* of rule groups,
 * one per prior/other study, unlike every other condition slot.
 */
function RuleGroupList({ slot, label, groups, disabled, onChange }) {
  const list = groups ?? [];
  return (
    <div className="hpv2-editor-list">
      <div className="hpv2-editor-list-header">
        <span className="hpv2-editor-list-label">{label}</span>
        {!disabled && (
          <DefaultButton
            iconProps={{ iconName: "Add" }}
            text={t("hanging-protocol.add-study")}
            onClick={() => onChange([...list, createEmptyRuleGroup(newNodeId())])}
          />
        )}
      </div>

      {list.length === 0 && <p className="hpv2-editor-note">{t("hanging-protocol.no-studies")}</p>}

      {list.map((group, index) => (
        <div className="hpv2-editor-list-item" key={group.id || index}>
          <ConditionBuilder
            slot={slot}
            label={`${label} ${index + 1}`}
            value={group}
            disabled={disabled}
            onChange={(next) => onChange(list.map((g, i) => (i === index ? next : g)))}
          />
          {!disabled && (
            <IconButton
              iconProps={{ iconName: "Delete" }}
              title={t("hanging-protocol.delete")}
              ariaLabel={`${t("hanging-protocol.delete")} ${label} ${index + 1}`}
              onClick={() => onChange(list.filter((_, i) => i !== index))}
            />
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * What the protocol is called.
 *
 * `field` is the wizard's Formik bag, which reports a field's error on the field
 * itself — see createFieldProps. Optional, so the composed editor and anything
 * rendering a protocol outside a form stay unchanged.
 */
export function ProtocolDetailsSection({ protocol, onChange, disabled = false, field = NO_FIELD_PROPS }) {
  if (!protocol) return null;
  const set = (patch) => onChange({ ...protocol, ...patch });

  if (disabled) {
    return (
      <div className="hpv2-editor-row">
        <ReadField label={t("hanging-protocol.name")} value={protocol.name} />
        <ReadField label={t("hanging-protocol.description")} value={protocol.description} />
      </div>
    );
  }

  return (
    <div className="hpv2-editor-row">
      <TextField
        label={t("hanging-protocol.name")}
        required
        value={protocol.name ?? ""}
        onChange={(_e, next) => set({ name: next ?? "" })}
        {...field("name")}
      />
      <TextField
        label={t("hanging-protocol.description")}
        value={protocol.description ?? ""}
        onChange={(_e, next) => set({ description: next ?? "" })}
        {...field("description")}
      />
    </div>
  );
}

/**
 * `NONE` is the unset state a MedDream category can arrive in, and it has no
 * radio of its own — the off mode stands in so the group is never blank. The
 * document keeps `NONE` until the author picks a mode, which is what the
 * planner reads as "leave the viewer's own sync alone".
 */
const selectedMode = (value, fallback) => (!value || value === "NONE" ? fallback : value);

/**
 * How scrolling and actions behave across the hung series.
 *
 * Both are segmented strips rather than dropdowns, matching the device strip a
 * group is set up with: there are only four modes each, and the choice reads as
 * a comparison — you pick one by seeing the alternatives beside it, not by
 * opening a list to find out what they are.
 *
 * Each strip is boxed in its own fieldset so the border says which segments
 * answer which question, and the legend names it via `labelId` rather than the
 * strip carrying a label of its own, so the name is not stated twice.
 *
 * Stacked rather than side by side: a four-segment strip wants the full width,
 * and in half of it the strip wrapped mid-way and stopped reading as one
 * control.
 */
export function ProtocolSyncSection({ protocol, onChange, disabled = false }) {
  if (!protocol) return null;
  const set = (patch) => onChange({ ...protocol, ...patch });

  /* The fieldset is there to say which segments answer which question. With the
     strips gone there is one answer each, already labelled, so the boxes would be
     two empty frames. */
  if (disabled) {
    return (
      <div className="hpv2-editor-row">
        <ReadField
          label={t("hanging-protocol.link-scroll")}
          value={readOption(
            LINK_SCROLL_SERIES_OPTIONS,
            selectedMode(protocol.linkScrollSeries, LINK_SCROLL_SERIES.OFF)
          )}
        />
        <ReadField
          label={t("hanging-protocol.sync-actions")}
          value={readOption(SYNC_ACTIONS_OPTIONS, selectedMode(protocol.syncActions, SYNC_ACTIONS.OFF))}
        />
      </div>
    );
  }

  return (
    <div className="hpv2-editor-stack">
      <fieldset className="hpv2-fieldset">
        <legend className="hpv2-fieldset-legend" id="hpv2-link-scroll-legend">
          {t("hanging-protocol.link-scroll")}
        </legend>
        <SegmentedField
          labelId="hpv2-link-scroll-legend"
          options={LINK_SCROLL_SERIES_OPTIONS}
          value={selectedMode(protocol.linkScrollSeries, LINK_SCROLL_SERIES.OFF)}
          disabled={disabled}
          onChange={(linkScrollSeries) => set({ linkScrollSeries })}
        />
      </fieldset>
      <fieldset className="hpv2-fieldset">
        <legend className="hpv2-fieldset-legend" id="hpv2-sync-actions-legend">
          {t("hanging-protocol.sync-actions")}
        </legend>
        <SegmentedField
          labelId="hpv2-sync-actions-legend"
          options={SYNC_ACTIONS_OPTIONS}
          value={selectedMode(protocol.syncActions, SYNC_ACTIONS.OFF)}
          disabled={disabled}
          onChange={(syncActions) => set({ syncActions })}
        />
      </fieldset>
    </div>
  );
}

/** Which studies this protocol hangs for. */
export function ProtocolConditionsSection({ protocol, onChange, disabled = false }) {
  if (!protocol) return null;

  return (
    <ConditionBuilder
      slot="studyConditions"
      label={t("hanging-protocol.study-conditions")}
      value={protocol.studyConditions}
      disabled={disabled}
      onChange={(next) => onChange({ ...protocol, studyConditions: next })}
    />
  );
}

/** Prior and comparison studies — standard groups only. */
export function ProtocolPriorsSection({ protocol, onChange, disabled = false }) {
  if (!protocol) return null;
  const set = (patch) => onChange({ ...protocol, ...patch });

  return (
    <>
      {/* Three toggles beside a text box, so this one keeps the bottom
          alignment the rest of the rows gave up — see hpv2-editor-row. */}
      <div className="hpv2-editor-row hpv2-editor-row-baseline">
        {disabled ? (
          <>
            <ReadField
              label={t("hanging-protocol.open-all-priors")}
              value={readYesNo(protocol.openAllPriors)}
            />
            <ReadField
              label={t("hanging-protocol.expand-all-priors")}
              value={readYesNo(protocol.expandAllPriors)}
            />
            <ReadField
              label={t("hanging-protocol.open-new-comparison")}
              value={readYesNo(protocol.openNewComparison)}
            />
            <ReadField
              label={t("hanging-protocol.open-max-priors")}
              value={String(protocol.openMaxPriors ?? 0)}
            />
          </>
        ) : (
          <>
            <Toggle
              label={t("hanging-protocol.open-all-priors")}
              checked={Boolean(protocol.openAllPriors)}
              onChange={(_e, checked) => set({ openAllPriors: Boolean(checked) })}
            />
            <Toggle
              label={t("hanging-protocol.expand-all-priors")}
              checked={Boolean(protocol.expandAllPriors)}
              onChange={(_e, checked) => set({ expandAllPriors: Boolean(checked) })}
            />
            <Toggle
              label={t("hanging-protocol.open-new-comparison")}
              checked={Boolean(protocol.openNewComparison)}
              onChange={(_e, checked) => set({ openNewComparison: Boolean(checked) })}
            />
            <TextField
              label={t("hanging-protocol.open-max-priors")}
              type="number"
              value={String(protocol.openMaxPriors ?? 0)}
              onChange={(_e, next) => set({ openMaxPriors: Number(next) || 0 })}
            />
          </>
        )}
      </div>

      <RuleGroupList
        slot="priorStudyConditions"
        label={t("hanging-protocol.prior-conditions")}
        groups={protocol.priorStudyConditions}
        disabled={disabled}
        onChange={(next) => set({ priorStudyConditions: next })}
      />
      <RuleGroupList
        slot="otherStudyConditions"
        label={t("hanging-protocol.other-conditions")}
        groups={protocol.otherStudyConditions}
        disabled={disabled}
        onChange={(next) => set({ otherStudyConditions: next })}
      />
    </>
  );
}

/**
 * The panel/viewport grid with the selected viewport's settings beside it.
 * Selection is local state: which cell you are editing is not part of the
 * protocol, so it must not make the draft dirty.
 *
 * The inspector shows one viewport at a time, which is the whole point of the
 * step and also the one place a field cannot simply carry its own error: a bad
 * Rotation on 2×2 is invisible while 1×1 is selected. So the wizard names the
 * viewport its validation failed on in `focus`, and this selects it — and
 * `invalidViewport` marks every other failing cell on the grid, so a protocol
 * with two bad viewports says so rather than surfacing them one Save at a time.
 */
export function ProtocolLayoutSection({
  protocol,
  onChange,
  disabled = false,
  showAdvanced = false,
  field = NO_FIELD_PROPS,
  focus = null,
  invalidViewport,
}) {
  const [selected, setSelected] = useState({ panelIndex: 0, viewportIndex: 0 });

  // A fresh object every time Save finds a problem, so pressing it twice on the
  // same viewport still brings the author back to it.
  useEffect(() => {
    if (focus) setSelected(focus);
  }, [focus]);

  if (!protocol) return null;

  const panels = protocol.panelsLayout?.panels ?? [];
  const selectedViewport =
    panels[selected.panelIndex]?.layout?.viewports?.[selected.viewportIndex] ?? null;

  const set = (patch) => onChange({ ...protocol, ...patch });

  const updateSelectedViewport = (nextViewport) =>
    set({
      panelsLayout: {
        ...protocol.panelsLayout,
        panels: panels.map((panel, panelIndex) =>
          panelIndex !== selected.panelIndex
            ? panel
            : {
                ...panel,
                layout: {
                  ...panel.layout,
                  viewports: panel.layout.viewports.map((viewport, viewportIndex) =>
                    viewportIndex === selected.viewportIndex ? nextViewport : viewport
                  ),
                },
              }
        ),
      },
    });

  /* The inspector edits one viewport and knows nothing about where it sits, so
     its relative field names are anchored to the selected cell here. */
  const viewportPath = `panelsLayout.panels[${selected.panelIndex}].layout.viewports[${selected.viewportIndex}]`;
  const viewportField = (relativeName) => field(`${viewportPath}.${relativeName}`);

  return (
    <div className="hpv2-editor-layout">
      <div className="hpv2-editor-layout-grid">
        <LayoutDesigner
          panelsLayout={protocol.panelsLayout}
          selected={selected}
          disabled={disabled}
          onSelect={setSelected}
          onChange={(next) => set({ panelsLayout: next })}
          invalidViewport={invalidViewport}
        />
      </div>
      <aside className="hpv2-editor-layout-inspector">
        <ViewportInspector
          viewport={selectedViewport}
          disabled={disabled}
          showAdvanced={showAdvanced}
          onChange={updateSelectedViewport}
          field={viewportField}
        />
      </aside>
    </div>
  );
}

export default function ProtocolEditor({
  protocol,
  kind = GROUP_KIND.STANDARD,
  onChange,
  disabled = false,
  showAdvanced = false,
}) {
  if (!protocol) return null;

  const isStandard = kind === GROUP_KIND.STANDARD;

  return (
    <div className="hpv2-editor">
      <ProtocolDetailsSection protocol={protocol} disabled={disabled} onChange={onChange} />
      <ProtocolConditionsSection protocol={protocol} disabled={disabled} onChange={onChange} />
      <ProtocolSyncSection protocol={protocol} disabled={disabled} onChange={onChange} />

      {isStandard && PRIORS_UI_ENABLED && (
        <section className="hpv2-editor-section">
          <h4 className="hpv2-editor-heading">{t("hanging-protocol.priors")}</h4>
          <ProtocolPriorsSection protocol={protocol} disabled={disabled} onChange={onChange} />
        </section>
      )}

      <section className="hpv2-editor-section">
        <h4 className="hpv2-editor-heading">{t("hanging-protocol.layout")}</h4>
        <ProtocolLayoutSection
          protocol={protocol}
          disabled={disabled}
          showAdvanced={showAdvanced}
          onChange={onChange}
        />
      </section>
    </div>
  );
}
